import axios, { AxiosError } from "axios";
import { FilePreviewApiError, FilePreviewErrorKind } from "../types";

export const PREVIEW_NOT_FOUND_MESSAGE = "Preview not found.";
export const PREVIEW_PAGE_NOT_FOUND_MESSAGE = "Preview page not found.";
export const BAD_REQUEST_MESSAGE = "Bad request";
export const INTERNAL_SERVER_ERROR_MESSAGE = "Internal server error";
export const PREVIEW_NOT_FOUND_MESSAGE_VI = "Chưa tìm thấy bản xem trước.";
export const PREVIEW_PAGE_NOT_FOUND_MESSAGE_VI = "Không tìm thấy trang xem trước.";
export const BAD_REQUEST_MESSAGE_VI =
  "Yêu cầu xem trước không hợp lệ. Vui lòng kiểm tra lại thao tác.";
export const INTERNAL_SERVER_ERROR_MESSAGE_VI =
  "Hệ thống đang tạm thời gặp sự cố. Vui lòng thử lại.";
export const UNKNOWN_PREVIEW_ERROR_MESSAGE_VI =
  "Không thể tải bản xem trước.";

function isPreviewApiError(error: unknown): error is FilePreviewApiError {
  return Boolean(
    error &&
      typeof error === "object" &&
      "kind" in error &&
      "message" in error &&
      "canRetry" in error,
  );
}

function isAxiosErrorWithResponse(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

function resolveErrorKind(
  status: number | null,
  message: string,
): FilePreviewErrorKind {
  if (status === 400) return "bad_request";
  if (status === 404 && message === PREVIEW_PAGE_NOT_FOUND_MESSAGE) {
    return "preview_page_not_found";
  }
  if (status === 404 && message === PREVIEW_NOT_FOUND_MESSAGE) {
    return "preview_not_found";
  }
  if (status === 500) return "server_error";
  return "unknown";
}

function resolveFallbackMessage(status: number | null, fallbackMessage: string): string {
  if (status === 400) return BAD_REQUEST_MESSAGE;
  if (status === 404) return PREVIEW_NOT_FOUND_MESSAGE;
  if (status === 500) return INTERNAL_SERVER_ERROR_MESSAGE;
  return fallbackMessage;
}

function resolveDisplayMessage(kind: FilePreviewErrorKind): string {
  switch (kind) {
    case "bad_request":
      return BAD_REQUEST_MESSAGE_VI;
    case "preview_not_found":
      return PREVIEW_NOT_FOUND_MESSAGE_VI;
    case "preview_page_not_found":
      return PREVIEW_PAGE_NOT_FOUND_MESSAGE_VI;
    case "server_error":
      return INTERNAL_SERVER_ERROR_MESSAGE_VI;
    default:
      return UNKNOWN_PREVIEW_ERROR_MESSAGE_VI;
  }
}

function createPreviewApiError(params: {
  status: number | null;
  code?: number | null;
  message: string;
  fallbackMessage: string;
}): FilePreviewApiError {
  const normalizedMessage =
    params.message.trim() ||
    resolveFallbackMessage(params.status, params.fallbackMessage);
  const kind = resolveErrorKind(params.status, normalizedMessage);

  return {
    status: params.status,
    code: params.code ?? null,
    kind,
    message: resolveDisplayMessage(kind),
    canRetry: kind === "server_error",
  };
}

function tryParseJson(rawValue: string): unknown {
  try {
    return JSON.parse(rawValue);
  } catch (_error) {
    return rawValue;
  }
}

async function readErrorPayload(payload: unknown): Promise<unknown> {
  if (typeof Blob !== "undefined" && payload instanceof Blob) {
    return tryParseJson(await payload.text());
  }

  if (typeof payload === "string") {
    return tryParseJson(payload);
  }

  return payload;
}

export function createPreviewNotFoundError(
  message = PREVIEW_NOT_FOUND_MESSAGE,
): FilePreviewApiError {
  return createPreviewApiError({
    status: 404,
    message,
    fallbackMessage: PREVIEW_NOT_FOUND_MESSAGE,
  });
}

export async function normalizePreviewApiError(
  error: unknown,
  fallbackMessage: string,
): Promise<FilePreviewApiError> {
  if (isPreviewApiError(error)) {
    return error;
  }

  if (!isAxiosErrorWithResponse(error)) {
    return createPreviewApiError({
      status: null,
      message: "",
      fallbackMessage,
    });
  }

  const responseData = await readErrorPayload(error.response?.data);
  const responseMessage =
    typeof responseData === "string"
      ? responseData
      : (responseData as any)?.message ?? "";
  const responseCode =
    typeof responseData === "string" ? null : (responseData as any)?.code ?? null;

  return createPreviewApiError({
    status: error.response?.status ?? null,
    code: responseCode,
    message: responseMessage,
    fallbackMessage,
  });
}
